import React from 'react';
import cls from 'classnames';
import range from 'lodash/range';
import { useTranslation } from 'react-i18next';
import { useSearchParamsAsObject } from '@/hooks/useSearchParamsAsObject.hook';

interface PaginationProps {
  page: number;
  limit: number;
  setPage?: (page: number) => void;
  total?: number;
  totalPage?: number;
  maxSiblings?: number;
  setParam?: (page: number) => void;
}

export default function Pagination({
  page,
  limit,
  // setPage = () => null,
  total,
  totalPage,
  maxSiblings = 2,
}: PaginationProps) {
  const { t } = useTranslation();
  const { replaceSearchParam } = useSearchParamsAsObject<{
    page: number;
    limit: number;
  }>();

  const setParam = (page = 1) => {
    replaceSearchParam({ page: String(page) });
  };
  const pageCount = React.useMemo(() => {
    if (!total && !totalPage) {
      return 1;
    }

    if (totalPage && totalPage > 0) {
      return totalPage;
    }

    if (!total || total < limit) {
      return 1;
    }

    return Math.ceil(total / limit);
  }, [limit, total, totalPage]);
  const setCurrentPage = React.useCallback(
    (nextPage: number) => {
      if (nextPage < 1) return;
      if (nextPage > pageCount) return;

      return setParam(nextPage);
    },
    [page, pageCount, limit, setParam],
  );

  const paginationRange = React.useMemo(() => {
    const pageNumbers = maxSiblings + 5;
    if (pageNumbers >= pageCount) {
      return range(1, pageCount + 1);
    }

    const leftIdx = Math.max(page - maxSiblings, 1);
    const rightIdx = Math.min(page + maxSiblings + 1, pageCount);

    const showLeft = leftIdx > maxSiblings;
    const showRight = rightIdx < pageCount - maxSiblings;

    // console.log({view, pageNumbers, pageCount, leftIdx, rightIdx, showLeft, showRight})

    if (!showLeft && showRight) {
      return [...range(1, 2 * maxSiblings + 2), '...', pageCount];
    }

    if (showLeft && !showRight) {
      return [1, '...', ...range(pageCount - 2 * maxSiblings - 2, pageCount)];
    }

    if (showLeft && showRight) {
      return [1, '...', ...range(leftIdx, rightIdx), '...', pageCount];
    }
  }, [page, pageCount, setCurrentPage]);

  return (
    <nav className='flex flex-wrap items-center justify-center space-x-2 max-w-full'>
      <button
        disabled={page < 2}
        onClick={() => setCurrentPage(page - 1)}
        className='text-gray-500 hover:text-blue-600 p-4 inline-flex items-center gap-2 rounded-md cursor-pointer disabled:pointer-events-none disabled:opacity-50'
      >
        <i aria-hidden='true' className='icon-angle-left' />
        <span className='sr-only'>{t('Previous')}</span>
      </button>
      {paginationRange?.map((item, idx) => (
        <button
          key={'pagination_' + idx}
          onClick={() => setCurrentPage(item as number)}
          disabled={item === '...'}
          className={cls(
            'w-10 h-10 p-4 inline-flex items-center text-sm font-medium rounded-full',
            item === page ? 'text-blue-600' : 'text-gray-500 hover:text-blue-400',
          )}
        >
          {item}
        </button>
      ))}
      {/* {Array.from({ length: pageCount })
        .map((i, k) => k + 1)
        .map((p) => (
          <button
            key={'pagination_' + p}
            onClick={() => setCurrentPage(p)}
            className={cls(
              'w-10 h-10 p-4 inline-flex items-center text-sm font-medium rounded-full cursor-pointer',
              p === view ? 'text-blue-600' : 'text-gray-500 hover:text-blue-400',
            )}
            aria-current='view'
          >
            {p}
          </button>
        ))} */}
      <button
        disabled={page + 1 > pageCount}
        onClick={() => setCurrentPage(page + 1)}
        className='text-gray-500 hover:text-blue-600 p-4 inline-flex items-center gap-2 rounded-md cursor-pointer disabled:pointer-events-none disabled:opacity-50'
      >
        <span className='sr-only'>{t('Next')}</span>
        <i aria-hidden='true' className='icon-angle-right' />
      </button>
    </nav>
  );
}
